import { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  TextField, IconButton, Alert, Snackbar,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { api } from '../../../api/client.js';
import type { IAppSetting } from '../../../../shared/types.js';
import { colors } from '../../../styles/theme.js';

/**
 * Die generische Key/Value-Tabelle. Slack-Keys liefert der Server hier bewusst
 * nicht mehr aus (eigener Bereich mit Maskierung), `configurator_visible` und
 * `playwright_target` sitzen als Schalter unter "Allgemein" (den zweiten
 * filtert und sperrt der Server zusaetzlich — nur live|staging sind gueltig).
 */
const HIDDEN_KEYS = ['configurator_visible', 'playwright_target'];

export default function KeySettingsPage() {
  const [settings, setSettings] = useState<IAppSetting[]>([]);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const loadSettings = async () => {
    const res = await api.get<{ settings: IAppSetting[] }>('/admin/settings');
    if (res.success) {
      setSettings(res.data.settings);
      const vals: Record<string, string> = {};
      res.data.settings.forEach((s) => { vals[s.key] = s.value; });
      setEditValues(vals);
    } else {
      setError(res.error.message);
    }
  };

  useEffect(() => { loadSettings(); }, []);

  const handleSave = async (key: string) => {
    const res = await api.patch(`/admin/settings/${key}`, { value: editValues[key] });
    if (res.success) {
      setSaved(true);
      loadSettings();
    } else {
      setError(res.error.message);
    }
  };

  const visible = settings.filter((s) => !HIDDEN_KEYS.includes(s.key));

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>Key Config</Typography>
      <Typography variant="body2" sx={{ mb: 2, color: colors.black60 }}>
        Freie Key/Value-Einstellungen der Anwendung. Änderungen greifen sofort und landen im Audit-Log.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper elevation={0} sx={{ border: `1px solid ${colors.silver}` }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: colors.lightGrey }}>
              <TableCell>Key</TableCell>
              <TableCell>Value</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((setting) => (
              <TableRow key={setting.key}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    {setting.key}
                  </Typography>
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    value={editValues[setting.key] ?? ''}
                    onChange={(e) => setEditValues({ ...editValues, [setting.key]: e.target.value })}
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    color="primary"
                    onClick={() => handleSave(setting.key)}
                    disabled={editValues[setting.key] === setting.value}
                  >
                    <SaveIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} sx={{ textAlign: 'center', py: 4 }}>Keine Settings vorhanden</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Snackbar open={saved} autoHideDuration={3000} onClose={() => setSaved(false)} message="Setting gespeichert!" />
    </Box>
  );
}
